import * as React from "react";
import { cn } from "../../lib/utils";
import { Button } from "./button";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { Clock } from "lucide-react";

function TimePicker({
  value,
  onChange,
  placeholder = "Select time",
  slots,
  disabled = false,
  className,
  "data-testid": testId,
}) {
  const [open, setOpen] = React.useState(false);

  const selectedLabel = React.useMemo(() => {
    if (!value) return null;
    const slot = (slots || []).find(s => s.value === value);
    return slot ? slot.label : value;
  }, [value, slots]);

  // Group slots by hour for grid display
  const hourGroups = React.useMemo(() => {
    const groups = {};
    (slots || []).forEach(s => {
      const h = parseInt(s.value.split(':')[0]);
      const ampm = h < 12 ? 'AM' : 'PM';
      const displayH = h === 0 ? 12 : h > 12 ? h - 12 : h;
      const key = `${displayH} ${ampm}`;
      if (!groups[key]) groups[key] = [];
      groups[key].push(s);
    });
    return Object.entries(groups);
  }, [slots]);

  return (
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          data-testid={testId}
          className={cn(
            "w-full justify-start text-left font-normal rounded-sm h-9 px-3 border-input",
            !value && "text-muted-foreground",
            className
          )}
        >
          <Clock className="mr-2 h-4 w-4 opacity-60" />
          {selectedLabel || placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0" align="start">
        <div className="max-h-[300px] overflow-y-auto p-2">
          {hourGroups.map(([hour, hourSlots]) => (
            <div key={hour} className="mb-1.5">
              <p className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold px-1 mb-1">{hour}</p>
              <div className="flex gap-1">
                {hourSlots.map(s => (
                  <button
                    key={s.value}
                    type="button"
                    onClick={() => { onChange(s.value); setOpen(false); }}
                    className={cn(
                      "flex-1 px-2 py-1.5 text-xs rounded-sm transition-colors",
                      value === s.value
                        ? "bg-[#002FA7] text-white font-semibold"
                        : "hover:bg-zinc-100 text-zinc-700"
                    )}
                  >
                    {s.label.split(' ')[0].split(':')[1] === '00' ? s.label : `:${s.label.split(':')[1].split(' ')[0]}`}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
        {value && (
          <div className="border-t border-zinc-100 p-1.5">
            <Button type="button" variant="ghost" size="sm" className="w-full text-xs text-zinc-500" onClick={() => { onChange(''); setOpen(false); }}>
              Clear
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

export { TimePicker };
